import dynamic from 'next/dynamic';
import { useState, useCallback, useEffect, useContext, createContext } from 'react';
import { ThemeProvider } from 'styled-components';

import { themes } from '@vimeo/iris/themes';

import { useViewer } from './use-viewer';
import { CommentsContainerPropsType } from '../CommentsCommon/CommentsContainer';

export const LoginJoinModal = dynamic(async () => {
  const { LoginJoinModal } = await import('@vimeo/ui/core');

  return { default: LoginJoinModal };
});

interface LoginModalState {
  type: 'login' | 'join';
  isShowing: boolean;
  xsrft: string;
  redirectUrl?: string;
  onDismiss: () => void;
  onSuccess: () => void;
}

interface LoginModalContext {
  modalState: LoginModalState;
  setModalState: (event: Partial<LoginModalState>) => void;
  toggleLoginModal: (event: boolean, type?: LoginModalState['type']) => void;
}

const defaultModalState: LoginModalState = {
  type: 'login',
  isShowing: false,
  xsrft: '',
  onDismiss: () =>
    console.warn('Without dismiss function, LoginJoinModal isShowing props might be out of sync'),
  onSuccess: () => console.warn('LoginJoinModal: onSuccess callback is not initialized'),
};

export const loginJoinModalContext = createContext<LoginModalContext>({
  modalState: defaultModalState,
  setModalState: () => new Error('Setter function is not initialized'),
  toggleLoginModal: () => new Error('Toggle function is not initialized'),
});

export const LoginModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState(defaultModalState);

  const setModalState = useCallback(
    (event: Partial<LoginModalState>) => setState((prev) => ({ ...prev, ...event })),
    [],
  );

  const toggleLoginModal = useCallback(
    (event: boolean, type?: LoginModalState['type']) => {
      setModalState({ isShowing: event, type: type || 'login' });
    },
    [setModalState],
  );

  return (
    <loginJoinModalContext.Provider value={{ modalState: state, setModalState, toggleLoginModal }}>
      {children}
    </loginJoinModalContext.Provider>
  );
};

export const LoginModal = () => {
  const { modalState } = useContext(loginJoinModalContext);
  return (
    <ThemeProvider theme={themes.light}>
      <LoginJoinModal {...modalState} />
    </ThemeProvider>
  );
};

export function useLoginModal() {
  const viewer = useViewer();
  const { modalState, setModalState, toggleLoginModal } = useContext(loginJoinModalContext);
  const setRedirectUrl = (redirectUrl: string) => setModalState({ redirectUrl });
  const redirectUrl = modalState.redirectUrl;

  useEffect(() => {
    if (!viewer) return;

    setModalState({
      xsrft: viewer.xsrft,
      onDismiss: () => toggleLoginModal(false),
      onSuccess: () => {
        redirectUrl ? window.location.assign(redirectUrl) : window.location.reload();
      },
    });
  }, [viewer, setModalState, toggleLoginModal, redirectUrl]);

  return { toggleLoginModal, setRedirectUrl };
}

export const withLoginModal = (
  CommentsContainerComponent: React.FC<CommentsContainerPropsType>,
) => {
  return (props: CommentsContainerPropsType) => {
    return (
      <LoginModalProvider>
        <CommentsContainerComponent {...props}></CommentsContainerComponent>
        <LoginModal />
      </LoginModalProvider>
    );
  };
};
